import { useRunActionState } from '@/hooks/useRunActionState';
import { useAuthStore } from '@/store/use-auth-store';
import { useChatStore } from '@/store/useChatStore';
import { getPromptIdByName } from '@/utils/promptUtils';
import { isSoulMapAppRoute } from '@/utils/soulmap-auth';
import { useEffect } from 'react';
import { useCreateOrUpdatePractice } from 'Src/hooks/queries/practices';
import useGetPrompts from 'Src/hooks/queries/prompts/useGetPrompts';
import { useNavigation } from './useNavigation';

interface PracticeResponse {
  id: string;
  name: string;
  description: string;
}

export const useInitializePractice = (
  defaultAgent?: string,
  options?: { promptsEnabled?: boolean }
) => {
  const { data: prompts, isLoading, isFetching, refetch } = useGetPrompts({
    enabled: options?.promptsEnabled ?? true,
  });
  const createPracticeMutation = useCreateOrUpdatePractice();
  const { setRunActionState } = useRunActionState();
  const orgId = useAuthStore((state) => state.orgId);
  const { navigateToTenantRoute } = useNavigation();
  const setLoading = useChatStore((state) => state.setLoading);

  const createPractice = async (prompt: { name: string }) => {
    try {
      setLoading(true);
      const response = await createPracticeMutation.mutateAsync({
        name: prompt.name,
        org_id: orgId,
      });

      if (response && typeof response === 'object' && 'id' in response) {
        const practiceResponse = response as PracticeResponse;
        setRunActionState({
          id: practiceResponse.id,
          actionTitle: prompt.name,
        });
        return practiceResponse;
      }

      throw new Error('Invalid response format');
    } catch (error) {
      console.error('Failed to create practice:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const createPracticeAndNavigate = async (prompt: { name: string }) => {
    const practiceResponse = await createPractice(prompt);
    const isSoulMap = isSoulMapAppRoute(window.location.pathname);
    const basePath = isSoulMap ? 'soulmap' : 'home';
    navigateToTenantRoute(
      `${basePath}/${practiceResponse.id}/run/?action=${encodeURIComponent(prompt.name)}&id=${practiceResponse.id}`
    );
  };

  useEffect(() => {
    if (!prompts?.results || !defaultAgent) return;

    const prompt = getPromptIdByName(prompts, defaultAgent);
    if (!prompt?.name) return;

    createPracticeAndNavigate({ name: prompt.name });
  }, [prompts, defaultAgent]);

  return {
    createPractice,
    createPracticeAndNavigate,
    prompts,
    isLoadingPrompts: isLoading,
    isFetchingPrompts: isFetching,
    refetchPrompts: refetch,
  };
};
